import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'
import * as d3 from 'd3'

class PixelTransition {
  /**
   * Media query that checks for the mobile breakpoint
   */
  static mobileQuery = window.matchMedia(`(max-width: ${GLOBAL_CONSTANTS.BREAKPOINTS.mobile}px)`)

  /**
   * Stores and array of rows, each containing a reference to the row container
   * and an array of boxes within that row.
   * @type {Array}
   */
  rows = []

  /**
   * Stores the number of rows being displayed when motion is on. Used to
   * prevent redrawing all boxes if the row count hasn't changed.
   */
  storedDynamicRowCount

  /**
   * A global eventemitter3 object
   */
  emitter = GLOBAL_CONSTANTS.UTILS.EMITTER

  /**
   * Helper for checking the mobile breakpoint
   */
  get isMobile() {
    return PixelTransition.mobileQuery.matches
  }

  /**
   * Helper for checking the site motion setting
   */
  get reducedMotion() {
    return document.documentElement.classList.contains('reduced-motion')
  }

  /**
   * Helper for getting the visible document width
   */
  get viewportWidth() {
    return document.documentElement.clientWidth
  }

  /**
   * Helper for getting the visible document height
   */
  get viewportHeight() {
    return document.documentElement.clientHeight
  }

  /**
   * Helper for getting the maximum document width (equivalent to 100lvw)
   */
  get maxViewportWidth() {
    return Math.max(this.viewportWidth, this.dummyElem.offsetWidth)
  }

  /**
   * Helper for getting the maximum document height (equivalent to 100lvh)
   */
  get maxViewportHeight() {
    return Math.max(this.viewportHeight, this.dummyElem.offsetHeight)
  }

  constructor(element) {
    this.element = element
    this.grid = this.element.querySelector('.js-pixel-transition-grid')
    this.dummyElem = this.element.querySelector('.js-pixel-transition-dummy')
    this.scrambleFactor = Number.parseFloat(this.element.dataset.scrambleFactor)
    this.colorInterpolator = d3[this.element.dataset.colorInterpolator](
      this.element.dataset.colorStart,
      this.element.dataset.colorEnd
    )

    this.observer = new IntersectionObserver(this.toggleScrollListenerForSection, {
      root: null,
      rootMargin: '0px',
      threshold: 0
    })

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.REDUCED_MOTION_CHANGE, this.setupBlocksForMotionSetting)

    this.setupBlocksForMotionSetting()
  }

  /**
   * Branches off of the reduced motion setting to set/unset observers and
   * listeners and then passes control to the constructBoxes function.
   */
  setupBlocksForMotionSetting = () => {
    this.element.dataset.dynamic = !this.reducedMotion
    this.storedDynamicRowCount = null

    if (this.reducedMotion) {
      this.observer.unobserve(this.element)

      PixelTransition.mobileQuery.addEventListener('change', this.constructBoxes)
      window.removeEventListener('resize', this.constructBoxes)
    } else {
      this.observer.observe(this.element)

      PixelTransition.mobileQuery.removeEventListener('change', this.constructBoxes)
      window.addEventListener('resize', this.constructBoxes)
    }

    this.constructBoxes()
  }

  /**
   * Calculates the correct number of columns for the size and then passes
   * control to the specific box constructor method for the current motion
   * setting.
   */
  constructBoxes = () => {
    const colCount = parseInt(this.element.dataset[this.isMobile ? 'mobileColumns' : 'desktopColumns'])

    if (this.reducedMotion) {
      this.constructStaticBoxes(colCount)
    } else {
      this.constructDynamicBoxes(colCount)
    }
  }

  /**
   * Constructs an animating grid of pixels that sit behind the content and are
   * revealed through a "cutout" between two sections.
   *
   * The rows are calculated to fill the entire viewport height plus overflow.
   * Rows are only regenerated when the viewport changes enough to change the
   * number of rows needed.
   *
   * Each box is given a start, end and pow attribute, which affect the rate at
   * which it animates on scroll. Each box transitions colors between values of
   * their start and end values, clipped to [0, 1], with start as low as -0.25
   * and end as high as 0.25. That value is then raised to the power of pow,
   * changing the curve of the transition.
   *
   * A number of pixels, set through the scrambleFactor variable, use a sine
   * wave pattern configured to go from 0 to 1 to 0 to 1, to add a flicker
   * effect to the transition. For these, start=0, end=1 and pow=1, to simplify
   * the curve.
   *
   * @param {Int} colCount
   */
  constructDynamicBoxes = (colCount) => {
    const rowCount = Math.ceil(this.maxViewportHeight / (this.viewportWidth / colCount))

    if (this.storedDynamicRowCount !== rowCount) {
      this.storedDynamicRowCount = rowCount

      this.resetGrid()

      this.element.style.setProperty('--row-count', rowCount)

      this.rows = []
      for (let r = 0; r < rowCount; r++) {
        const row = document.createElement('div')
        row.classList.add('pixel-transition-row')

        const boxes = []
        for (let c = 0; c < colCount; c++) {
          const box = document.createElement('div')
          box.classList.add('pixel-transition-box')

          if (Math.random() < this.scrambleFactor / 100) {
            box.dataset.useInOut = 'true'
            box.dataset.start = 0
            box.dataset.end = 1
            box.dataset.pow = 1
          } else {
            box.dataset.start = Math.random() > 0.5 ? Math.random() * -0.25 : 0
            box.dataset.end = Math.random() > 0.5 ? 1 + (Math.random() * 0.25) : 1
            box.dataset.pow = Math.random() > 0.5 ? 1 + (Math.random() * 5) : 1
          }

          boxes.push(box)
          row.appendChild(box)
        }

        this.grid.appendChild(row)

        this.rows.push({
          element: row,
          boxes: boxes
        })
      }
    }

    this.setNegativeGridOffset()
    this.updateDynamicPixels()
  }

  /**
   * Constructs a randomized, static pixel grid that sits between two sections.
   *
   * Uses Paul Chaikin's <paul.chaikin@instrument.com> color randomization
   * algorithm.
   *
   * @param {Int} colCount
   */
  constructStaticBoxes = (colCount) => {
    this.resetGrid()

    const rowCount = parseInt(this.element.dataset[this.isMobile ? 'rmMobileRows' : 'rmDesktopRows'])

    for (let r = 0; r < rowCount; r++) {
      const row = document.createElement('div')
      row.classList.add('pixel-transition-row')

      for (let c = 0; c < colCount; c++) {
        const box = document.createElement('div')
        box.classList.add('pixel-transition-box')

        const colorSpread = this.constructColorSpread(rowCount * colCount, Math.round(colCount * 0.5))
        const minColorIndex = (r * colCount) + (colCount / 2)
        const seed = Math.random() * 2 - 1
        const rand = seed * (colCount * (this.scrambleFactor / 10))
        let colorIndex = rand + minColorIndex
        colorIndex = Math.max(0, colorIndex)
        colorIndex = Math.min(colorSpread.length - 1, colorIndex)
        colorIndex = Math.round(colorIndex)

        box.style.setProperty('--pixel-box-background', colorSpread[colorIndex])

        row.appendChild(box)
      }

      this.grid.appendChild(row)
    }
  }

  /**
   * Updates the background color for each pixel in the dynamic grid.
   */
  updateDynamicPixels = () => {
    const scrollY = window.scrollY

    const {
      height: wrapperHeight,
      top: wrapperTop
    } = this.element.getBoundingClientRect()

    for (const { boxes, element: row } of this.rows) {
      const {
        top: rowTop,
        height: rowHeight
      } = row.getBoundingClientRect()

      const scrollMax = wrapperTop + window.scrollY - rowTop
      const scrollMin = scrollMax - wrapperHeight + rowHeight

      const range = scrollMax - scrollMin
      const amount = scrollY - scrollMin
      const factor = (amount / range) - 1

      if (factor >= 0 && factor <= 1) {
        row.dataset.needsUpdate = true
      }

      for (const box of boxes) {
        const boxStart = Number.parseFloat(box.dataset.start)
        const boxEnd = Number.parseFloat(box.dataset.end)
        const boxPow = Number.parseFloat(box.dataset.pow)

        let boxFactor = Math.max(0, (boxEnd - boxStart) * Math.pow(factor, boxPow)) + boxStart
        boxFactor = Math.min(1, Math.max(0, boxFactor))
        boxFactor = isNaN(boxFactor) ? 0 : boxFactor

        if ('useInOut' in box.dataset) {
          boxFactor = -0.5 * Math.cos(boxFactor * Math.PI * 3) + 0.5
        }

        box.style.setProperty('--pixel-box-background', this.colorInterpolator(boxFactor))
      }
    }
  }

  /**
   * Constructs a color spread for the static version of the transition.
   * @param {Int} total - Number of colors to create
   * @param {Int} overfill - The number of repeat instances of the start and end colors to use
   */
  constructColorSpread = (total, overfill) => {
    const repeatStart = Array(overfill).fill(this.colorInterpolator(0))
    const repeatEnd = Array(overfill).fill(this.colorInterpolator(1))

    const spread = []
    const unrepeatedColorCount = total - repeatStart.length - repeatEnd.length
    for (let i = 0; i < unrepeatedColorCount; i++) {
      spread.push(this.colorInterpolator(i / unrepeatedColorCount))
    }

    return [].concat(repeatStart, spread, repeatEnd)
  }

  /**
   * Callback for the element's IntersectionObserver that primarily adds/removes
   * event listeners.
   */
  toggleScrollListenerForSection = (sections) => {
    this.element.dataset.isIntersecting = sections[0]?.isIntersecting || false
    if (sections[0]?.isIntersecting) {
      document.addEventListener('scroll', this.updateDynamicPixels)
      window.visualViewport?.addEventListener('resize', this.forcePixelRepaint)
      this.updateDynamicPixels()
    } else {
      document.removeEventListener('scroll', this.updateDynamicPixels)
      window.visualViewport?.removeEventListener('resize', this.forcePixelRepaint)
    }
  }

  /**
   * Set's the negative top offset for the grid container to vertically center
   * it in the viewport.
   */
  setNegativeGridOffset = () => {
    this.element.style.setProperty(
      '--negative-block-inset',
      `${(this.maxViewportHeight - this.grid.getBoundingClientRect().height) / 2}px`
    )
  }

  /**
   * Forces a row of pixels to visually update.
   *
   * When the viewport resizes on mobile Safari (i.e. when the Chrome expands or
   * contracts), updates to an element's style property are paused. Manually
   * updating the DOM forces a change.
   *
   * This function runs as a callback on visualViewport's resize event. In the
   * regular scroll handler, rows that are visible to the user are tagged with
   * data-needs-update="true". This checks that, and updates innerHTML with a
   * random value to force the style update.
   */
  forcePixelRepaint = () => {
    for (const { boxes, element: row } of this.rows) {
      if ('needsUpdate' in row.dataset) {
        for (const box of boxes) {
          box.innerHTML = Math.random()
        }
        delete row.dataset.needsUpdate
      }
    }
  }

  /**
   * Helper which clears the grid
   */
  resetGrid = () => {
    this.grid.innerHTML = ''
  }
}

export default {
  selector: '.js-pixel-transition',
  Source: PixelTransition
}
